import type { TanyaIntent, TanyaSuggestion } from "./types";

/** Infer the "surface" enum from a URL path. */
export function inferSurface(path: string): TanyaIntent["surface"] {
  if (!path || typeof path !== "string") return "unknown";
  if (path.startsWith("/solutions/c/")) return "cluster";
  if (path.startsWith("/solutions/p/")) return "pillar";
  if (path.startsWith("/products/")) return "product";
  if (path.startsWith("/resources")) return "resource";
  if (path === "/") return "homepage-hero";
  return "page";
}

/** Map a CTA mode key to a goal. */
export function inferGoal(mode?: string): TanyaIntent["goal"] {
  switch (mode) {
    case "request-demo":
    case "speak-expert":
      return "demo";
    case "executive-report":
      return "exec-report";
    case "partner-enquiry":
      return "partner";
    case "ask":
      return "ask";
    default:
      return "ask";
  }
}

/**
 * Build a complete, well-formed TanyaIntent even when the caller
 * provided nothing. Always safe to inject into the edge function.
 */
export function synthesiseIntent(
  path: string,
  partial?: Partial<TanyaIntent> & { context?: string; mode?: string },
): TanyaIntent {
  const safePath = typeof path === "string" && path.length > 0 ? path : "/";
  const surface = partial?.surface ?? inferSurface(safePath);
  const topic =
    partial?.topic ??
    (partial?.context ? { type: "page" as const, title: String(partial.context) } : undefined);
  return {
    surface,
    topic,
    parentPillar: partial?.parentPillar,
    goal: partial?.goal ?? inferGoal(partial?.mode),
    questionSeed: partial?.questionSeed,
    pageUrl: partial?.pageUrl ?? safePath,
    mode: partial?.mode,
  };
}

/** Defensive: drop malformed suggestions and cap to 3. */
export function normaliseSuggestions(input: unknown): TanyaSuggestion[] {
  if (!Array.isArray(input)) return [];
  const out: TanyaSuggestion[] = [];
  for (const raw of input) {
    if (!raw || typeof raw !== "object") continue;
    const r = raw as Record<string, unknown>;
    const label = typeof r.label === "string" ? r.label.trim() : "";
    const action = r.action === "send-message" || r.action === "open-cta" ? r.action : null;
    const payload = typeof r.payload === "string" ? r.payload.trim() : "";
    if (!label || !action || !payload) continue;
    out.push({ label: label.slice(0, 60), action, payload });
    if (out.length === 3) break;
  }
  return out;
}

/**
 * Last-resort opening line + chips when the AI gateway fails or
 * intent is missing entirely. Mirrored on the edge function.
 */
export function getFallbackOpenLine(
  intent?: TanyaIntent | null,
  visitorName?: string | null,
): { greeting: string; suggestions: TanyaSuggestion[] } {
  const namePart = visitorName ? `, ${visitorName}` : "";
  const topicTitle = intent?.topic?.title?.trim();
  const goal = intent?.goal ?? "ask";

  if (goal === "demo") {
    return {
      greeting: `Happy to set up a demo${namePart}. A few quick details and I'll get the right person to reach out.`,
      suggestions: [
        { label: "Book a demo", action: "open-cta", payload: "request-demo" },
        { label: "Speak to an expert", action: "open-cta", payload: "speak-expert" },
      ],
    };
  }
  if (goal === "exec-report") {
    return {
      greeting: `I can prepare a tailored Executive Report${namePart}. What's the focus?`,
      suggestions: [
        { label: "Generate Executive Report", action: "open-cta", payload: "executive-report" },
        { label: "Tell me what's included", action: "send-message", payload: "What's included in the Executive Report?" },
      ],
    };
  }

  if (goal === "partner") {
    const partner = topicTitle || "this partner";
    return {
      greeting: `Great choice${namePart} — let's get you connected with **${partner}**. I just need a few details so they can follow up.`,
      suggestions: [
        { label: `Talk to ${partner}`, action: "open-cta", payload: "partner-enquiry" },
        { label: "What does this partner do?", action: "send-message", payload: `Tell me more about ${partner}.` },
      ],
    };
  }

  if (topicTitle) {
    return {
      greeting: `Happy to help with **${topicTitle}**${namePart}. What would you like to know?`,
      suggestions: [
        { label: `How does ${topicTitle} work?`, action: "send-message", payload: `Can you explain how ${topicTitle} works?` },
        { label: "Book a demo", action: "open-cta", payload: "request-demo" },
      ],
    };
  }

  return {
    greeting: `Hi${namePart} — what can I help you with today?`,
    suggestions: [
      { label: "Tell me about Tango Extend", action: "send-message", payload: "Tell me about Tango Extend" },
      { label: "Book a demo", action: "open-cta", payload: "request-demo" },
    ],
  };
}